import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import {
  EmptyState,
  LoadingState,
  TypeaheadAvatar,
  TypeaheadDropdownContainer,
  TypeaheadDropdownHeader,
  TypeaheadInfo,
  TypeaheadOption,
  TypeaheadPrimaryText,
  TypeaheadScrollArea,
} from 'features/richText/components/TypeaheadDropdownStyles'
import { $createTextNode, TextNode } from 'lexical'
import React, { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import { createUnitMentionNode } from './UnitMentionNode'
import { MentionableEntry } from './useMentionableEntries'

const TRIGGER_CHAR = '@'

class UnitTypeaheadOption extends MenuOption {
  entry: MentionableEntry
  constructor(entry: MentionableEntry) {
    super(entry.name || entry.id)
    this.entry = entry
  }
}

export interface UnitMentionPluginProps {
  entries: MentionableEntry[]
  isLoading: boolean
  maxSuggestions?: number
}

export const UnitMentionPlugin: React.FC<UnitMentionPluginProps> = ({
  entries,
  isLoading,
  maxSuggestions = 10,
}) => {
  const [queryString, setQueryString] = useState<string | null>(null)

  const options = useMemo(() => {
    if (queryString === null) return []
    const q = queryString.toLowerCase().trim()
    return entries
      .filter((entry) => {
        if (!q) return true
        const name = entry.name?.toLowerCase() ?? ''
        const id = entry.id?.toLowerCase() ?? ''
        return name.includes(q) || id.includes(q)
      })
      .sort((a, b) => {
        const aName = a.name?.toLowerCase() ?? ''
        const bName = b.name?.toLowerCase() ?? ''
        const aStarts = aName.startsWith(q)
        const bStarts = bName.startsWith(q)
        if (aStarts && !bStarts) return -1
        if (!aStarts && bStarts) return 1
        return aName.localeCompare(bName)
      })
      .slice(0, maxSuggestions)
      .map((entry) => new UnitTypeaheadOption(entry))
  }, [queryString, entries, maxSuggestions])

  const onSelectOption = useCallback(
    (
      selectedOption: UnitTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      const mentionNode = createUnitMentionNode(selectedOption.entry)
      if (nodeToReplace) {
        nodeToReplace.replace(mentionNode)
      }
      mentionNode.insertAfter($createTextNode(' '))
      closeMenu()
    },
    []
  )

  const checkForMentionMatch = useBasicTypeaheadTriggerMatch(TRIGGER_CHAR, {
    minLength: 0,
    allowWhitespace: true,
  })

  return (
    <LexicalTypeaheadMenuPlugin<UnitTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        if (!anchorElementRef.current) return null
        return createPortal(
          <TypeaheadDropdownContainer
            $minWidth='16rem'
            $maxWidth='20rem'
            $placement='top'>
            <TypeaheadDropdownHeader>Mention Asset</TypeaheadDropdownHeader>
            <TypeaheadScrollArea>
              {isLoading ? (
                <LoadingState>Loading assets...</LoadingState>
              ) : options.length === 0 ? (
                <EmptyState>No assets found</EmptyState>
              ) : (
                options.map((option, index) => (
                  <TypeaheadOption
                    key={option.entry.id}
                    ref={option.setRefElement}
                    $isSelected={selectedIndex === index}
                    onClick={() => {
                      setHighlightedIndex(index)
                      selectOptionAndCleanUp(option)
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}>
                    <TypeaheadAvatar $shape='rounded'>
                      {(option.entry.name || option.entry.id)[0]?.toUpperCase()}
                    </TypeaheadAvatar>
                    <TypeaheadInfo>
                      <TypeaheadPrimaryText>
                        {option.entry.name || option.entry.id}
                      </TypeaheadPrimaryText>
                    </TypeaheadInfo>
                  </TypeaheadOption>
                ))
              )}
            </TypeaheadScrollArea>
          </TypeaheadDropdownContainer>,
          anchorElementRef.current
        )
      }}
    />
  )
}
